import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Mail, Search, UserPlus } from 'lucide-react'

import {
  addMember,
  createInviteEmail,
  createInviteLink,
  searchStudioUsers,
  type ProductionDepartment,
  type ProductionInvite,
  type ProductionMember,
  type StudioUserSearchHit,
} from '@/api/studio-production'
import { MEMBER_ROLES, type MemberRole } from '@/features/studio/lib/production-constants'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import {
  ProtoBtn,
  ProtoInput,
  ProtoLabel,
  ProtoModal,
  ProtoSelect,
  ProtoTabs,
} from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { cn } from '@/shared/lib/utils'

export function inviteAbsoluteUrl(relative: string): string {
  if (relative.startsWith('http')) return relative
  return `${window.location.origin}${relative.startsWith('/') ? relative : `/${relative}`}`
}

export function InviteMemberModal({
  open,
  onClose,
  productionId,
  canInvite,
  hasCollab = canInvite,
  departments = [],
  defaultDepartmentId = '',
  onInvited,
}: {
  open: boolean
  onClose: () => void
  productionId: string
  canInvite: boolean
  hasCollab?: boolean
  departments?: ProductionDepartment[]
  defaultDepartmentId?: string
  onInvited?: () => void
}) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'search' | 'email' | 'link'>('search')
  const [role, setRole] = useState<MemberRole>('contributor')
  const [departmentId, setDepartmentId] = useState('')
  const [query, setQuery] = useState('')
  const [hits, setHits] = useState<StudioUserSearchHit[]>([])
  const [searching, setSearching] = useState(false)
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [email, setEmail] = useState('')
  const [linkInvite, setLinkInvite] = useState<ProductionInvite | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setDepartmentId(defaultDepartmentId)
    setError(null)
    setLinkInvite(null)
    setSelectedUserId(null)
    setQuery('')
    setHits([])
    setEmail('')
  }, [open, defaultDepartmentId])

  async function runSearch(q: string) {
    if (!hasCollab || q.trim().length < 2) {
      setHits([])
      return
    }
    setSearching(true)
    try {
      const res = await searchStudioUsers(productionId, q.trim())
      setHits(res.items)
    } catch {
      setHits([])
    } finally {
      setSearching(false)
    }
  }

  async function handleInviteUser() {
    if (selectedUserId == null) {
      setError(t('team.invitePickUser'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await addMember(productionId, {
        user_id: selectedUserId,
        role,
        ...(departmentId ? { department_id: departmentId } : {}),
      })
      onInvited?.()
      onClose()
      resetForm()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.inviteFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleInviteEmail() {
    const addr = email.trim()
    if (!addr) {
      setError(t('team.inviteEmailRequired'))
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const out = await createInviteEmail(productionId, {
        email: addr,
        role,
        ...(departmentId ? { department_id: departmentId } : {}),
      })
      setLinkInvite(out)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.inviteFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  async function handleGenerateLink() {
    setSubmitting(true)
    setError(null)
    try {
      const out = await createInviteLink(productionId, {
        role,
        ...(departmentId ? { department_id: departmentId } : {}),
      })
      setLinkInvite(out)
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'team.inviteFailed'))
    } finally {
      setSubmitting(false)
    }
  }

  function resetForm() {
    setQuery('')
    setHits([])
    setSelectedUserId(null)
    setEmail('')
    setLinkInvite(null)
    setDepartmentId('')
  }

  const upgradeHref = `/subscription?category_code=team&return=${encodeURIComponent(window.location.href)}`

  const footerPrimary =
    tab === 'search' ? (
      <ProtoBtn size="sm" onClick={() => void handleInviteUser()} disabled={!canInvite || submitting}>
        <UserPlus className="h-4 w-4" />
        {submitting ? t('team.inviting') : t('team.inviteConfirm')}
      </ProtoBtn>
    ) : tab === 'email' && !linkInvite ? (
      <ProtoBtn size="sm" onClick={() => void handleInviteEmail()} disabled={!canInvite || submitting}>
        <Mail className="h-4 w-4" />
        {submitting ? t('team.inviting') : t('team.inviteSendEmail')}
      </ProtoBtn>
    ) : tab === 'link' && !linkInvite ? (
      <ProtoBtn size="sm" onClick={() => void handleGenerateLink()} disabled={!canInvite || submitting}>
        {submitting ? t('team.inviting') : t('team.inviteGenerateLink')}
      </ProtoBtn>
    ) : null

  return (
    <ProtoModal
      open={open}
      onClose={() => {
        onClose()
        resetForm()
      }}
      title={t('team.inviteTitle')}
      wide
      footer={
        <>
          <ProtoBtn size="sm" variant="secondary" onClick={onClose}>
            {t('common.close')}
          </ProtoBtn>
          {hasCollab ? footerPrimary : null}
        </>
      }
    >
      {!hasCollab ? (
        <div className="mb-4 rounded-xl border border-amber-500/20 bg-amber-500/5 px-4 py-3 text-xs text-amber-100/90">
          <p>{t('team.collabRequired')}</p>
          <a href={upgradeHref} className="mt-2 inline-block text-indigo-300 underline underline-offset-2">
            {t('team.upgradeCollab')}
          </a>
        </div>
      ) : !canInvite ? (
        <div className="mb-4 rounded-xl border border-rose-500/20 bg-rose-500/5 px-4 py-3 text-xs text-rose-100/90">
          {t('team.seatsFull')}
        </div>
      ) : null}

      <div className="mb-4 grid grid-cols-2 gap-3">
        {departments.length > 0 ? (
          <div>
            <ProtoLabel>{t('team.fieldDepartment')}</ProtoLabel>
            <ProtoSelect
              value={departmentId}
              onChange={(e) => setDepartmentId(e.target.value)}
              disabled={!hasCollab}
            >
              <option value="">{t('team.noDepartment')}</option>
              {departments.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </ProtoSelect>
          </div>
        ) : null}
        <div className={departments.length > 0 ? '' : 'col-span-2'}>
          <ProtoLabel>{t('team.fieldRole')}</ProtoLabel>
          <ProtoSelect value={role} onChange={(e) => setRole(e.target.value as MemberRole)} disabled={!hasCollab}>
            {MEMBER_ROLES.map((r) => (
              <option key={r} value={r}>
                {t(`production.roles.${r}`, { defaultValue: r })}
              </option>
            ))}
          </ProtoSelect>
        </div>
      </div>

      <ProtoTabs
        tabs={[
          { id: 'search' as const, label: t('team.inviteTabSearch') },
          { id: 'email' as const, label: t('team.inviteTabEmail') },
          { id: 'link' as const, label: t('team.inviteTabLink') },
        ]}
        active={tab}
        onChange={(id) => {
          setTab(id)
          setError(null)
          setLinkInvite(null)
        }}
      />

      <div className="mt-4">
        {error ? (
          <p className="mb-3 text-xs text-rose-300">{t(error, { defaultValue: error })}</p>
        ) : null}

        {linkInvite ? (
          <div className="rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-3">
            <p className="text-[11px] text-emerald-300/80">{t('team.inviteLinkReady')}</p>
            {linkInvite.email_sent ? (
              <p className="mt-1 text-[11px] text-emerald-300/70">{t('team.inviteEmailSent')}</p>
            ) : linkInvite.email && !linkInvite.email_sent ? (
              <p className="mt-1 text-[11px] text-amber-200/80">{t('team.inviteEmailNotSent')}</p>
            ) : null}
            <div className="mt-2 flex gap-2">
              <ProtoInput readOnly value={inviteAbsoluteUrl(linkInvite.invite_url)} className="text-xs" />
              <ProtoBtn
                size="sm"
                variant="secondary"
                onClick={() => void navigator.clipboard.writeText(inviteAbsoluteUrl(linkInvite.invite_url))}
              >
                {t('team.copyLink')}
              </ProtoBtn>
            </div>
          </div>
        ) : null}

        {!linkInvite && tab === 'search' ? (
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <ProtoInput
                className="pl-9"
                placeholder={t('team.inviteSearchPlaceholder')}
                value={query}
                onChange={(e) => {
                  const v = e.target.value
                  setQuery(v)
                  setSelectedUserId(null)
                  void runSearch(v)
                }}
                disabled={!hasCollab}
              />
            </div>
            {searching ? <p className="text-[11px] text-zinc-500">{t('team.searching')}</p> : null}
            {hits.map((u) => (
              <Button
                key={u.user_id}
                type="button"
                variant="outline"
                onClick={() => setSelectedUserId(u.user_id)}
                className={cn(
                  'h-auto w-full justify-between rounded-xl px-3 py-2.5 text-left font-normal',
                  selectedUserId === u.user_id
                    ? 'border-indigo-500/40 bg-indigo-500/10'
                    : 'border-border bg-muted/30 hover:border-indigo-500/30',
                )}
              >
                <span className="text-sm text-zinc-200">
                  {u.display_name || u.username}
                  <span className="ml-2 text-[11px] text-zinc-500">@{u.username}</span>
                </span>
                <span className="text-[11px] text-zinc-500">#{u.user_id}</span>
              </Button>
            ))}
            {query.trim().length >= 2 && !searching && hits.length === 0 ? (
              <p className="text-[11px] text-zinc-500">{t('team.inviteNoResults')}</p>
            ) : null}
          </div>
        ) : null}

        {!linkInvite && tab === 'email' ? (
          <div className="space-y-3">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-zinc-500" />
              <ProtoInput
                className="pl-9"
                type="email"
                placeholder={t('team.inviteEmailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={!hasCollab}
              />
            </div>
            <p className="text-[11px] text-zinc-500">{t('team.inviteEmailHint')}</p>
          </div>
        ) : null}

        {!linkInvite && tab === 'link' ? (
          <p className="text-sm text-zinc-400">{t('team.inviteLinkHint')}</p>
        ) : null}
      </div>
    </ProtoModal>
  )
}

export function memberDisplayName(m: ProductionMember): string {
  return m.display_name?.trim() || m.username?.trim() || `User #${m.user_id}`
}

export function memberInitials(m: ProductionMember): string {
  const name = memberDisplayName(m)
  return name.slice(0, 2).toUpperCase()
}
